import type { Appointment, ScheduleEntry, TaskListItem } from '@myapp/contract'
import {
  clampSpanToShift,
  formatTimeHHmm,
  packIntoLanes,
  resolveBlockTimes,
  shiftWindow,
  toBoardSpan,
  type BoardSpan,
} from '../../../shared/lib/schedule'
import { getAbsenceCategoryLabel } from '../../../entities/absence'
import { workOrderAddress } from '../../../shared/helpers/address'
import { isWorkingDay } from '../../../shared/lib/workingDays'
import type { TechnicianScheduleView } from '../../../entities/schedule'

/**
 * One day of the worker calendar, as both the renderer and the conflict check see it.
 *
 * This is the worker-side counterpart of `disposition-board`'s `buildRows`, and it exists for the
 * same reason: drawing and colliding used to be two separate passes over the same data
 * (`overlapLayout.ts` and `checkPauseCollisions.ts`), and they disagreed. An absence was drawn
 * clamped to the shift but collided against the whole day; the break was drawn 30 minutes long
 * whatever the technician's profile said; scheduled tasks were drawn but never collided with at
 * all, so a Pause dropped on top of one passed every client check and was rejected by the server
 * in silence.
 *
 * Everything below produces `BoardSpan`s in the shared minute domain. This widget is vertical and
 * the board is horizontal, but that difference lives entirely in `spanTopPx`/`spanHeightPx` — the
 * geometry, lane packing and collision rules are the same code on both sides.
 */

export type CalendarBlockKind = 'APPOINTMENT' | 'TASK' | 'ABSENCE' | 'BREAK'

export interface CalendarBlock {
  /**
   * `${kind}:${id}` — a **string**, deliberately. The map this replaces was keyed by a flat owner
   * id across all four kinds, so an appointment and a task that happened to share an id
   * overwrote each other's layout. Same reasoning as `packIntoLanes`.
   */
  key: string
  kind: CalendarBlockKind
  /** Occupancy in minutes from the window start. Already shift-clamped for ABSENCE. */
  span: BoardSpan
  needsRescheduling: boolean
  /** What the conflict list calls this block. */
  label: string
  /** The time the conflict list shows next to it. */
  timeLabel: string
  /** ABSENCE and BREAK carry their entry; appointments and tasks carry their owner. */
  entry?: ScheduleEntry
  appointment?: Appointment
  task?: TaskListItem
  /**
   * ABSENCE only. An absence covering several technicians is one row per technician, but one
   * parent — moving it moves everybody's, so the policy makes those inert.
   */
  technicianCount: number
  /** ABSENCE only: fills the shift end to end, which is what "ganztägig" means on a calendar. */
  coversWholeShift: boolean
  /** BREAK only: the Pause's length from the server's entry, profile default as fallback. */
  durationMinutes?: number
  /**
   * `0` for ABSENCE/BREAK. Fed to `detectTransfers` — the numbers as drawn (`resolveBlockTimes`'
   * `actual ?? planned`), never re-derived from planned times.
   */
  travelMinutes: number
  departureMinutes: number
}

export interface DayBlocks {
  blocks: CalendarBlock[]
  /** `key` → lane index. Feeds the cascade indent; nothing is ever fully hidden. */
  lanes: Map<string, number>
  working: boolean
  shift: { startMin: number; endMin: number } | null
  /** The day's break, when the server placed one and no absence swallows the shift. */
  breakBlock: { entry: ScheduleEntry; durationMinutes: number } | null
}

const EMPTY: DayBlocks = {
  blocks: [],
  lanes: new Map(),
  working: true,
  shift: null,
  breakBlock: null,
}

/** The work start a human would name — "der Termin ist um 09:00", not when the van leaves. */
function workStartLabel(schedule: ScheduleEntry): string {
  return formatTimeHHmm(schedule.actualWorkStartsAt ?? schedule.plannedWorkStartsAt)
}

function sameLocalDay(iso: string, dayIso: string): boolean {
  const d = new Date(iso)
  const day = new Date(`${dayIso}T00:00:00`)
  return (
    d.getFullYear() === day.getFullYear() &&
    d.getMonth() === day.getMonth() &&
    d.getDate() === day.getDate()
  )
}

export function buildDayBlocks(input: {
  technicianData: TechnicianScheduleView | null
  scheduledTasks: TaskListItem[]
  dayIso: string
  /**
   * The clock, floored to the minute (`useClockMinute`). With it, a job whose open phase has run
   * past its plan is drawn out to now rather than to the planned end — see `resolveBlockTimes`.
   * Omitted, blocks end where the server says they do.
   */
  nowMs?: number
}): DayBlocks {
  const { technicianData, scheduledTasks, dayIso, nowMs } = input
  if (!technicianData) return EMPTY

  const working = isWorkingDay(technicianData.workingDays, dayIso)
  const shift = shiftWindow(technicianData.shiftStart, technicianData.shiftEnd)
  const blocks: CalendarBlock[] = []

  /*
   * `schedule.startsAt` / `endsAt` are the occupancy range — travel and departure included —
   * derived by a DB CHECK as `COALESCE(actual, planned) ∓ durations`, row-locally. Reading them
   * directly replaces the three hand-rolled COALESCE copies this widget used to carry, each of
   * which gated on `status === 'COMPLETED'` while the server never consults status at all. An
   * appointment in progress was drawn from planned times the server had already superseded.
   *
   * It also makes day filtering fall out for free: `toBoardSpan` returns `null` for anything
   * outside this day's drawn window, so a block running over midnight appears in both columns
   * instead of only the one its work start happens to land in.
   */
  for (const appointment of technicianData.appointments) {
    if (appointment.schedule.needsRescheduling) continue
    const times = resolveBlockTimes(appointment.schedule, nowMs)
    const span = toBoardSpan(times.liveStartsAt, times.liveEndsAt, dayIso)
    if (!span) continue

    blocks.push({
      key: `APPOINTMENT:${appointment.id}`,
      kind: 'APPOINTMENT',
      span,
      needsRescheduling: false,
      label: workOrderAddress(appointment.workOrder?.external) ?? 'Termin',
      timeLabel: workStartLabel(appointment.schedule),
      appointment,
      technicianCount: 1,
      coversWholeShift: false,
      travelMinutes: times.travelMinutes,
      departureMinutes: times.departureMinutes,
    })
  }

  for (const task of scheduledTasks) {
    const schedule = task.schedule
    if (!schedule || schedule.needsRescheduling) continue
    const times = resolveBlockTimes(schedule, nowMs)
    const span = toBoardSpan(times.liveStartsAt, times.liveEndsAt, dayIso)
    if (!span) continue

    blocks.push({
      key: `TASK:${task.id}`,
      kind: 'TASK',
      span,
      needsRescheduling: false,
      label: task.title,
      timeLabel: workStartLabel(schedule),
      task,
      technicianCount: 1,
      coversWholeShift: false,
      travelMinutes: times.travelMinutes,
      departureMinutes: times.departureMinutes,
    })
  }

  const absencesById = new Map(technicianData.absences.map((absence) => [absence.id, absence]))

  for (const entry of technicianData.timeManagementEntries) {
    if (entry.needsRescheduling) continue
    const raw = toBoardSpan(entry.startsAt, entry.endsAt, dayIso)
    if (!raw) continue

    /*
     * Redrawn onto the shift, exactly as `toAbsenceSpan` does on the board. A day-long Urlaub is
     * stored 00:00–24:00 but means "this technician is not available while they would have been
     * working", and drawing it over the small hours would swamp the column. On a non-working day
     * it is dropped entirely — the whole column is already shaded.
     *
     * Doing it here, once, is what stops the drawn block and the collision check disagreeing.
     */
    if (!working) continue
    const span = shift ? clampSpanToShift(raw, shift) : raw
    if (!span) continue

    const absence = entry.absenceId != null ? absencesById.get(entry.absenceId) : undefined

    blocks.push({
      key: `ABSENCE:${entry.id ?? entry.startsAt}`,
      kind: 'ABSENCE',
      span,
      needsRescheduling: false,
      label: getAbsenceCategoryLabel(entry.category ?? absence?.category ?? null),
      timeLabel: formatTimeHHmm(entry.startsAt),
      entry,
      technicianCount: absence?.technicians.length ?? 1,
      coversWholeShift:
        shift != null && span.startMin <= shift.startMin && span.endMin >= shift.endMin,
      travelMinutes: 0,
      departureMinutes: 0,
    })
  }

  /*
   * The break comes from the server's own BREAK entry — materialized or projected — so its length
   * is whatever `TechnicianProfile.breakDurationMinutes` actually is. Every previous copy of this
   * assumed 30 minutes and disagreed with the server for anyone on a different profile.
   *
   * Suppressed when an absence fills the shift: there is no break on a day off.
   */
  const swallowedByAbsence = blocks.some(
    (block) => block.kind === 'ABSENCE' && block.coversWholeShift,
  )
  const breakEntry = technicianData.breakOverrides.find((candidate) =>
    sameLocalDay(candidate.startsAt, dayIso),
  )

  let breakBlock: DayBlocks['breakBlock'] = null
  if (working && breakEntry && !swallowedByAbsence) {
    const span = toBoardSpan(breakEntry.startsAt, breakEntry.endsAt, dayIso)
    const durationMinutes =
      Math.round(
        (new Date(breakEntry.endsAt).getTime() - new Date(breakEntry.startsAt).getTime()) / 60_000,
      ) || technicianData.breakDurationMinutes

    breakBlock = { entry: breakEntry, durationMinutes }

    if (span) {
      blocks.push({
        key: 'BREAK',
        kind: 'BREAK',
        span,
        needsRescheduling: false,
        label: 'Pause',
        timeLabel: formatTimeHHmm(breakEntry.startsAt),
        entry: breakEntry,
        technicianCount: 1,
        coversWholeShift: false,
        durationMinutes,
        travelMinutes: 0,
        departureMinutes: 0,
      })
    }
  }

  const { lanes } = packIntoLanes(blocks.map((block) => ({ key: block.key, span: block.span })))

  return { blocks, lanes, working, shift, breakBlock }
}
